import type { FlatMapping } from "@/types/ui"

export interface Positioned {
  x: number
  y: number
}

export interface FlowSource extends Positioned {
  id: string
  entity: string
  field: string
}

export interface FlowTarget extends Positioned {
  id: string
  field: string
  /** First segment of the destination path, used to group the right-hand column */
  section: string
  mapped: boolean
  invalid: boolean
  /** Set on a summary row that stands in for a section's hidden unmapped destinations; the row's label is built from the count at the render site */
  collapsedCount?: number
}

export interface FlowJunction extends Positioned {
  id: string
  valueSet: string
}

export interface FlowLink {
  id: string
  sourceId: string
  targetId: string
  /** Value-set junction the ribbon passes through, if any */
  junctionId: string | null
  converter: string | null
  valueSet: string | null
  invalid: boolean
  mapping: FlatMapping
}

export interface FlowSectionLabel extends Positioned {
  section: string
}

export interface MappingFlow {
  sources: FlowSource[]
  targets: FlowTarget[]
  junctions: FlowJunction[]
  links: FlowLink[]
  sections: FlowSectionLabel[]
  geometry: FlowGeometry
  /** Drawing height in px */
  height: number
  stats: { mapped: number; unmapped: number; invalid: number; valueSets: number; entities: string[] }
}

export interface MappingFlowOptions {
  /** Draw every unmapped destination as its own row (default) or fold them into one summary row per section */
  includeUnmapped?: boolean
  /** Drawing width in px; the columns scale with it (default 880) */
  width?: number
}

export interface FlowGeometry {
  width: number
  sourceX: number
  junctionX: number
  targetX: number
  /** Characters that fit in a label column */
  labelChars: number
}

/** Row metrics in px, independent of width */
export const FLOW_ROWS = {
  row: 22,
  groupGap: 18,
  sectionHeader: 16,
  padTop: 20,
  padBottom: 20,
}

/**
 * Section name for a destination field with no dotted prefix. It is a translation key
 * rather than a label: this module has no locale, so the canvas resolves it when drawing.
 */
export const FLOW_UNSECTIONED_KEY = "mappingFlow.sections.fields"

const DEFAULT_WIDTH = 880
const MONO_CHAR_PX = 6.6

/** Column x positions for a drawing width: labels sit outside the ribbon area on each side */
export function flowGeometry(width = DEFAULT_WIDTH): FlowGeometry {
  const w = Math.max(360, width)
  return { width: w, sourceX: Math.round(w * 0.36), junctionX: Math.round(w * 0.5), targetX: Math.round(w * 0.64), labelChars: Math.max(12, Math.floor((w * 0.36 - 24) / MONO_CHAR_PX)) }
}

/**
 * The entity a source path belongs to is the mapping's entity; a dotted path ("address.city",
 * "jobOrder.onSite") is a nested path within it, not another entity. An entity-name prefix
 * ("Placement.status" on the Placement entity) is stripped so the label is not repeated.
 */
export function splitSourceField(sourceField: string, entity: string): { entity: string; field: string } {
  const prefix = `${entity}.`
  return { entity, field: sourceField.startsWith(prefix) ? sourceField.slice(prefix.length) : sourceField }
}

function sectionOf(destinationField: string): string {
  const dot = destinationField.indexOf(".")
  return dot > 0 ? destinationField.slice(0, dot) : FLOW_UNSECTIONED_KEY
}

function targetRow(m: FlatMapping, x: number): FlowTarget {
  return { id: `t:${m.destinationField}`, field: m.destinationField, section: sectionOf(m.destinationField), mapped: m.isMapped, invalid: !!m.isInvalid, x, y: 0 }
}

/** Destination rows in section order; hidden unmapped rows fold into one grey summary row per section */
function buildTargets(mappings: FlatMapping[], includeUnmapped: boolean, x: number): FlowTarget[] {
  const bySection = new Map<string, FlatMapping[]>()
  for (const m of mappings) {
    const section = sectionOf(m.destinationField)
    const list = bySection.get(section)
    if (list) list.push(m)
    else bySection.set(section, [m])
  }
  const targets: FlowTarget[] = []
  for (const [section, members] of Array.from(bySection).sort(([a], [b]) => a.localeCompare(b))) {
    const sorted = members.sort((a, b) => a.destinationField.localeCompare(b.destinationField))
    const shown = includeUnmapped ? sorted : sorted.filter((m) => m.isMapped)
    targets.push(...shown.map((m) => targetRow(m, x)))
    const hidden = sorted.length - shown.length
    if (hidden > 0) {
      targets.push({ id: `t:${section}:unmapped`, field: "", section, mapped: false, invalid: false, collapsedCount: hidden, x, y: 0 })
    }
  }
  return targets
}

/** Stacks the target rows top-to-bottom with a header line above each section; returns the section labels */
function placeTargets(targets: FlowTarget[], targetX: number): FlowSectionLabel[] {
  const { row, groupGap, sectionHeader, padTop } = FLOW_ROWS
  const sections: FlowSectionLabel[] = []
  let y = padTop
  let last: string | null = null
  for (const target of targets) {
    if (target.section !== last) {
      if (last !== null) y += groupGap
      sections.push({ section: target.section, x: targetX, y })
      y += sectionHeader
      last = target.section
    }
    target.y = y + row / 2
    y += row
  }
  return sections
}

/** Puts each item at its preferred height, then pushes rows apart so none overlap */
function settleColumn<T extends Positioned>(items: T[], preferredY: (item: T) => number): void {
  const { row, padTop } = FLOW_ROWS
  items.forEach((item) => (item.y = preferredY(item)))
  items.sort((a, b) => a.y - b.y)
  let floor = padTop + row / 2
  for (const item of items) {
    item.y = Math.max(item.y, floor)
    floor = item.y + row
  }
}

function mean(values: number[]): number {
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : FLOW_ROWS.padTop
}

/**
 * Lays out one mapping as a bipartite flow in 2D: ATS source fields on the left, FBO
 * destination fields on the right grouped by section, value-mapping sets as junctions
 * in the middle. Each source sits level with the destinations it feeds so ribbons stay
 * short; unmapped destinations still appear (or fold into a per-section count) so gaps
 * are visible at a glance.
 */
export function buildMappingFlow(mappings: FlatMapping[], primaryEntity: string, options: MappingFlowOptions = {}): MappingFlow {
  const includeUnmapped = options.includeUnmapped ?? true
  const geometry = flowGeometry(options.width)
  const sources = new Map<string, FlowSource>()
  const junctions = new Map<string, FlowJunction>()
  const links: FlowLink[] = []

  const targets = buildTargets(mappings, includeUnmapped, geometry.targetX)
  const sections = placeTargets(targets, geometry.targetX)
  const targetY = new Map(targets.map((t) => [t.id, t.y]))

  for (const m of mappings) {
    if (!m.isMapped || !m.sourceField) continue
    const { entity, field } = splitSourceField(m.sourceField, m.entityName ?? primaryEntity)
    const sourceId = `s:${entity}.${field}`
    if (!sources.has(sourceId)) sources.set(sourceId, { id: sourceId, entity, field, x: geometry.sourceX, y: 0 })
    let junctionId: string | null = null
    if (m.valueMappingSetName) {
      junctionId = `j:${m.valueMappingSetName}`
      if (!junctions.has(junctionId)) junctions.set(junctionId, { id: junctionId, valueSet: m.valueMappingSetName, x: geometry.junctionX, y: 0 })
    }
    links.push({
      id: `${sourceId}->${m.destinationField}`,
      sourceId,
      targetId: `t:${m.destinationField}`,
      junctionId,
      converter: m.dataTypeConverter ?? null,
      valueSet: m.valueMappingSetName ?? null,
      invalid: !!m.isInvalid,
      mapping: m,
    })
  }

  const sourceList = Array.from(sources.values())
  settleColumn(sourceList, (s) => mean(links.filter((l) => l.sourceId === s.id).map((l) => targetY.get(l.targetId) ?? 0)))
  const sourceY = new Map(sourceList.map((s) => [s.id, s.y]))

  const junctionList = Array.from(junctions.values())
  settleColumn(junctionList, (j) => mean(links.filter((l) => l.junctionId === j.id).flatMap((l) => [sourceY.get(l.sourceId) ?? 0, targetY.get(l.targetId) ?? 0])))

  const bottom = Math.max(...targets.map((t) => t.y), ...sourceList.map((s) => s.y), ...junctionList.map((j) => j.y), FLOW_ROWS.padTop)

  return {
    sources: sourceList,
    targets,
    junctions: junctionList,
    links,
    sections,
    geometry,
    height: bottom + FLOW_ROWS.row / 2 + FLOW_ROWS.padBottom,
    stats: {
      mapped: mappings.filter((m) => m.isMapped).length,
      unmapped: mappings.filter((m) => !m.isMapped).length,
      invalid: mappings.filter((m) => m.isInvalid).length,
      valueSets: junctionList.length,
      entities: Array.from(new Set(sourceList.map((s) => s.entity))).sort(),
    },
  }
}

/** SVG path for a ribbon: a smooth S-curve, bending through the junction when there is one */
export function ribbonPath(from: Positioned, to: Positioned, via: Positioned | null): string {
  const curve = (a: Positioned, b: Positioned) => {
    const bend = (b.x - a.x) * 0.5
    return `C ${a.x + bend} ${a.y}, ${b.x - bend} ${b.y}, ${b.x} ${b.y}`
  }
  return via ? `M ${from.x} ${from.y} ${curve(from, via)} ${curve(via, to)}` : `M ${from.x} ${from.y} ${curve(from, to)}`
}
